import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { LS_ROLE } from "@/constants/storageKeys";

export function ProtectedRoute({
  role: requiredRole,
  children,
}: {
  role: "candidate" | "hr";
  children: ReactNode;
}) {
  const location = useLocation();
  const role = localStorage.getItem(LS_ROLE);

  if (!role || role !== requiredRole) {
    // HR signs in on its own route, so an unauthenticated /hr lands on the HR
    // form rather than the public candidate login.
    const loginPath = requiredRole === "hr" ? "/hr/login" : "/login";
    return (
      <Navigate
        to={`${loginPath}?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  return <>{children}</>;
}

/**
 * Gate for the HR sign-in page. Signed-in HR goes straight to the ATS, and a
 * signed-in candidate is sent to their portal — they have no business on the
 * HR login, which is the point of separating the two pages.
 */
export function HrLoginRoute({ children }: { children: ReactNode }) {
  const role = localStorage.getItem(LS_ROLE);
  if (role === "hr") return <Navigate to="/hr" replace />;
  if (role === "candidate") return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

export function GuestRoute({ children }: { children: ReactNode }) {
  const role = localStorage.getItem(LS_ROLE);
  if (role === "candidate") return <Navigate to="/portal" replace />;
  if (role === "hr") return <Navigate to="/hr" replace />;
  return <>{children}</>;
}

/**
 * Keeps signed-in HR inside the ATS: landing on the public careers home — by
 * typing the URL, a stale bookmark, or deleting `/hr` off the address bar —
 * sends them back to `/hr`. Candidates and logged-out visitors pass through
 * untouched, so the job board stays public.
 *
 * `?preview=1` deliberately opts out, so HR can still check how a posting looks
 * to a candidate without signing out or opening a private window.
 *
 * Reads localStorage rather than provider state so the redirect resolves during
 * the first render — reading async state would flash the job board first. Like
 * the other guards here this is navigation convenience, not access control; the
 * backend is what actually protects HR data.
 */
export function HrHomeRedirect({ children }: { children: ReactNode }) {
  const location = useLocation();
  const previewing = new URLSearchParams(location.search).get("preview") === "1";
  if (!previewing && localStorage.getItem(LS_ROLE) === "hr") {
    // replace, not push: otherwise Back lands on `/`, redirects again, and traps them.
    return <Navigate to="/hr" replace />;
  }
  return <>{children}</>;
}

/** Requires any authenticated session (HR or candidate). */
export function AuthenticatedRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const role = localStorage.getItem(LS_ROLE);
  if (!role) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  return <>{children}</>;
}
